import React, { useEffect, useRef, useState } from "react";
import {
  Navbar,
  InputGroup,
  FormControl,
  Container,
  Dropdown,
  Modal,
  Button,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faBars,
  faTrashCan,
  faChevronDown,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { Slider } from "@mui/material";
import logo from "assets/logo.png";
import { useBreakpoints } from "hooks/useBreakPoints";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCartStore } from "../../../../zustand/cartStore";
import "./CustomNavbarClients.scss";
import UserIcon from "components/icons/UserIcon";
import HeartIcon from "components/icons/HeartIcon";
import CartIcon from "components/icons/CartIcon";
import { useAuthStore } from "../../../../zustand/loginStore";
import UserDropdown from "./UserDropdown";
import {
  createAuthenticatedAxiosInstance,
  fetchUserProfile,
  fetchPublicCategories,
  fetchPublicSizes,
  fetchPublicProducts,
} from "api/auth";
import { useUserStore } from "../../../../zustand/userStore";
import { PublicCategory } from "interfaces/ICategory";
import { Size } from "interfaces/ISize";

const CustomNavbarClients: React.FC = () => {
  const { isMobile, isTablet } = useBreakpoints();
  const navigate = useNavigate();
  const cartItems = useCartStore((state) => state.items);
  const removeItem = useCartStore((state) => state.removeItem);
  const [showArrow, setShowArrow] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams] = useSearchParams();
  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const { token, userData, isLoggedIn, logout, setUserData } = useAuthStore();
  const { favoriteProducts, favoritesLoaded, loadFavorites, resetFavorites, imageTimestamp } = useUserStore();
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [profileFetched, setProfileFetched] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [mobileSearchTerm, setMobileSearchTerm] = useState("");
  const [categories, setCategories] = useState<PublicCategory[]>([]);
  const [sizes, setSizes] = useState<Size[]>([]);
  const [allSizes, setAllSizes] = useState<Size[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [selectedSizes, setSelectedSizes] = useState<number[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 5000]);
  const [isFilteringSizes, setIsFilteringSizes] = useState(false);

  // Extract the first name of the user if available
  const firstName = userData?.name ? userData.name.split(" ")[0] : "";

  useEffect(() => {
    const fetchUserProfileData = async () => {
      if (isLoggedIn() && token && !profileFetched) {
        try {
          const axiosInstance = createAuthenticatedAxiosInstance(token);
          const profileData = await fetchUserProfile(axiosInstance);

          // Update the store with the user data including image_url
          setUserData(profileData.user); // Assuming profileData.user contains the user data
          setProfileFetched(true);
          // Actualizar el timestamp para forzar la recarga de la imagen
          // setImageTimestamp(Date.now());
        } catch (error) {
          console.error("Error fetching user profile:", error);
          setProfileFetched(true); // Still mark as fetched to prevent continuous retries
        }
      }
    };

    fetchUserProfileData();
  }, [isLoggedIn, token, profileFetched, setUserData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target as Node)
      ) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const queryParam = searchParams.get("q");
    if (queryParam) {
      setSearchTerm(queryParam);
    }
  }, [searchParams]);

  /**
   * Effect hook to load favorites automatically when user is logged in
   * This centralizes favorite loading in the navbar since it's used everywhere
   */
  useEffect(() => {
    if (token && isLoggedIn() && !favoritesLoaded) {
      loadFavorites(token);
    }
  }, [
    token,
    isLoggedIn,
    favoritesLoaded,
    loadFavorites,
    favoriteProducts.length,
  ]);

  /**
   * Additional effect to ensure favorites are loaded after profile fetch
   */
  useEffect(() => {
    if (token && isLoggedIn() && profileFetched && !favoritesLoaded) {
      loadFavorites(token);
    }
  }, [token, isLoggedIn, profileFetched, favoritesLoaded, loadFavorites]);

  /**
   * Force reload favorites if we have token but no favorites and they should be loaded
   */
  useEffect(() => {
    if (
      token &&
      isLoggedIn() &&
      favoritesLoaded &&
      favoriteProducts.length === 0
    ) {
      loadFavorites(token);
    }
  }, [
    token,
    isLoggedIn,
    favoritesLoaded,
    favoriteProducts.length,
    loadFavorites,
  ]);

  /**
   * Clean favorites when user logs out (no token but still has favorites)
   */
  useEffect(() => {
    if (!token && favoriteProducts.length > 0) {
      resetFavorites();
    }
  }, [token, favoriteProducts.length, resetFavorites]);

  /**
   * Load categories and sizes for mobile filters
   */
  useEffect(() => {
    const loadFiltersData = async () => {
      try {
        const [categoriesData, sizesData] = await Promise.all([
          fetchPublicCategories(),
          fetchPublicSizes(),
        ]);
        setCategories(categoriesData);
        setAllSizes(sizesData); // Guardar todas las tallas para poder filtrar
        setSizes(sizesData); // Inicialmente mostrar todas las tallas
      } catch (error) {
        console.error("Error loading filter data:", error);
      }
    };

    if (isMobile) {
      loadFiltersData();
    }
  }, [isMobile]);

  // Efecto para filtrar tallas cuando cambian las categorías seleccionadas
  useEffect(() => {
    if (allSizes.length > 0) {
      // Usar una función async interna
      const updateSizes = async () => {
        await filterSizesByCategories(selectedCategories);
      };
      updateSizes();
    }
  }, [selectedCategories, allSizes]);

  const CustomToggle = React.forwardRef<HTMLDivElement, any>(
    ({ onClick }, ref) => (
      <div
        ref={ref}
        onClick={(e) => {
          e.preventDefault();
          onClick(e);
        }}
        className="cart-toggle"
      >
        <CartIcon
          className="mx-2"
          size={isMobile ? "2rem" : "2.5rem"}
          color="black"
          strokeWidth={12}
        />
        {totalItems > 0 && <span className="cart-badge">{totalItems}</span>}
      </div>
    )
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
    }
  };

  const handleUserIconClick = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate("/");
    setShowUserDropdown(false);
    resetFavorites(); // Reset favorites data properly
  };

  // Navigate to specific profile sections
  const navigateToProfile = (section?: string) => {
    if (section === "login") {
      navigate("/login");
    } else if (section === "register") {
      navigate("/register");
    } else if (section) {
      navigate(`/profile/${section}`, { state: section });
    } else {
      navigate("/profile", { state: "profile" });
    }
    setShowUserDropdown(false);
  };

  // Handle mobile search
  const handleMobileSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (mobileSearchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(mobileSearchTerm)}`);
      setShowMobileSearch(false);
      setMobileSearchTerm("");
    }
  };

  // Handle mobile filters toggle
  const toggleMobileFilters = () => {
    setShowMobileFilters(!showMobileFilters);
  };

  // Handle category change
  const handleCategoryChange = (categoryId: number) => {
    setSelectedCategories((prev) => {
      const newCategories = prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId];
      
      // Si no hay categorías seleccionadas, limpiar también las tallas seleccionadas
      if (newCategories.length === 0) {
        setSelectedSizes([]);
      }
      
      // Filtrar tallas según las categorías seleccionadas
      filterSizesByCategories(newCategories);
      
      return newCategories;
    });
  };

  // Función para filtrar tallas según las categorías seleccionadas
  const filterSizesByCategories = async (categoryIds: number[]) => {
    try {
      setIsFilteringSizes(true);
      
      if (categoryIds.length === 0) {
        // Si no hay categorías seleccionadas, mostrar todas las tallas
        setSizes(allSizes);
        return;
      }

      // Obtener productos de las categorías seleccionadas
      const params: any = {
        categories: categoryIds.join(","),
        limit: 1000, // Obtener muchos productos para tener todas las tallas
      };

      const response = await fetchPublicProducts(params);
      const products = response.products;

      // Extraer todas las tallas únicas de los productos
      const availableSizeNames = new Set<string>();
      products.forEach(product => {
        product.attributes.forEach(attribute => {
          attribute.sizes.forEach(size => {
            availableSizeNames.add(size.size);
          });
        });
      });

      // Filtrar las tallas disponibles por nombre
      const filteredSizes = allSizes.filter(size => availableSizeNames.has(size.name));
      setSizes(filteredSizes);

      // Remover tallas seleccionadas que ya no están disponibles
      setSelectedSizes(prev => prev.filter(sizeId => {
        const size = allSizes.find(s => s.id === sizeId);
        return size && availableSizeNames.has(size.name);
      }));
    } catch (error) {
      console.error("Error filtering sizes by categories:", error);
      // En caso de error, mostrar todas las tallas
      setSizes(allSizes);
    } finally {
      setIsFilteringSizes(false);
    }
  };

  // Handle size change
  const handleSizeChange = (sizeId: number) => {
    setSelectedSizes((prev) => {
      if (prev.includes(sizeId)) {
        return prev.filter((id) => id !== sizeId);
      }
      return [...prev, sizeId];
    });
  };

  // Handle price range change
  const handlePriceRangeChange = (event: any, newValue: number | number[]) => {
    const [min, max] = newValue as [number, number];
    setPriceRange([min, Math.max(min, max)]);
  };

  // Apply filters and navigate to search
  const applyFilters = () => {
    const searchParams = new URLSearchParams();

    if (mobileSearchTerm.trim()) {
      searchParams.append("q", mobileSearchTerm);
    }

    if (selectedCategories.length > 0) {
      searchParams.append("categories", selectedCategories.join(","));
    }

    if (selectedSizes.length > 0) {
      searchParams.append("sizes", selectedSizes.join(","));
    }

    if (priceRange[0] > 0 || priceRange[1] < 5000) {
      searchParams.append("minPrice", priceRange[0].toString());
      searchParams.append("maxPrice", priceRange[1].toString());
    }

    const queryString = searchParams.toString();
    const url = queryString ? `/search?${queryString}` : "/search";
    
    navigate(url);
    setShowMobileFilters(false);
    setMobileSearchTerm("");
    setSelectedCategories([]);
    setSelectedSizes([]);
    setPriceRange([0, 5000]);
  };

  // Component for authenticated user profile
  const AuthenticatedUserProfile = () => (
    <div ref={userDropdownRef} className="user-profile-container">
      <div className="user-profile-button" onClick={handleUserIconClick}>
        <div className="profile-image-container">
          {userData?.image_url ? (
            <img
              src={`${userData.image_url}?t=${imageTimestamp}`}
              alt="Perfil"
              className="profile-image"
            />
          ) : (
            <UserIcon className="profile-icon" size="2.5rem" color="black" />
          )}
        </div>
        {!isMobile && (
          <div className="user-info">
            <div className="user-greeting">Hola, {firstName}</div>
            <div className="user-account">
              Mi cuenta{" "}
              <FontAwesomeIcon icon={faChevronDown} className="account-arrow" />
            </div>
          </div>
        )}
      </div>

      {showUserDropdown && (
        <UserDropdown
          isVisible={true}
          onClose={() => setShowUserDropdown(false)}
          isAuthenticated={true}
          onLogout={handleLogout}
          onNavigateToProfile={navigateToProfile}
        />
      )}
    </div>
  );

  return (
    <Navbar
      style={{ backgroundColor: "#FBE584", padding: "0" }}
      className="justify-content-between"
    >
      <Navbar.Brand href="#" className="d-flex align-items-center">
        {isMobile ? (
          <>
            <FontAwesomeIcon
              icon={faBars}
              style={{
                height: isMobile ? "1.5rem" : "2rem",
                cursor: "pointer",
                marginLeft: "0.5rem",
                marginRight: "0.5rem",
              }}
              onClick={toggleMobileFilters}
            />
            {isLoggedIn() ? (
              <div style={{ display: "flex", alignItems: "center" }}>
                <div className="d-flex align-items-center">
                  {userData?.image_url ? (
                    <img
                      src={`${userData.image_url}?t=${imageTimestamp}`}
                      alt="Perfil"
                      style={{
                        width: "2rem",
                        height: "2rem",
                        borderRadius: "50%",
                        objectFit: "cover",
                        cursor: "pointer",
                        marginLeft: "0.5rem",
                      }}
                      onClick={handleUserIconClick}
                    />
                  ) : (
                    <UserIcon
                      color="black"
                      size={isMobile ? "2rem" : "2.5rem"}
                      style={{
                        marginLeft: "0.5rem",
                      }}
                      onClick={handleUserIconClick}
                    />
                  )}
                </div>
                {showUserDropdown && (
                  <div
                    style={{
                      position: "absolute",
                      top: isMobile ? "-15%" : "100%",
                      right: isMobile ? "10%" : "0",
                      zIndex: 1000,
                    }}
                  >
                    <UserDropdown
                      isVisible={true}
                      onClose={() => setShowUserDropdown(false)}
                      isAuthenticated={true}
                      onLogout={handleLogout}
                      onNavigateToProfile={navigateToProfile}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div ref={userDropdownRef} style={{ position: "relative" }}>
                <UserIcon
                  className="mx-2"
                  size={isMobile ? "2rem" : "2.5rem"}
                  color="black"
                  onClick={handleUserIconClick}
                />
                {showUserDropdown && (
                  <div
                    style={{
                      position: "absolute",
                      top: isMobile ? "10%" : "100%",
                      right: isMobile ? "-150%" : "0",
                      zIndex: 1000,
                    }}
                  >
                    <UserDropdown
                      isVisible={true}
                      onClose={() => setShowUserDropdown(false)}
                      onNavigateToProfile={navigateToProfile}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <img
            src={logo}
            alt="Logo de la Boutique"
            style={{ width: "5rem", marginLeft: "1.5rem" }}
            onClick={() => navigate("/")}
          />
        )}
      </Navbar.Brand>
      {isMobile ? (
        <img
          src={logo}
          alt="Logo de la Boutique"
          style={{ width: "4rem", marginLeft: "2rem", cursor: "pointer" }}
          onClick={() => navigate("/")}
        />
      ) : (
        <Container
          className="mx-auto"
          style={{ width: isTablet ? "50%" : "25%" }}
        >
          <InputGroup>
            <FormControl
              type="text"
              placeholder="Buscar productos"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch(e)}
              style={{ borderRadius: "20px 0 0 20px" }}
            />
            <InputGroup.Text
              style={{
                borderRadius: "0 20px 20px 0",
                display: "flex",
                alignItems: "center",
                padding: "0 10px",
                cursor: "pointer",
              }}
              onClick={handleSearch}
            >
              <FontAwesomeIcon icon={faSearch} />
            </InputGroup.Text>
          </InputGroup>
        </Container>
      )}
      <div
        style={{
          marginRight: isMobile ? "1rem" : "1.7rem",
          display: "flex",
          alignItems: "center",
        }}
      >
        {isMobile ? (
          <>
            <FontAwesomeIcon
              icon={faSearch}
              className="mx-2"
              style={{
                height: isMobile ? "1.5rem" : "2rem",
                cursor: "pointer",
              }}
              onClick={() => setShowMobileSearch(true)}
            />
          </>
        ) : isLoggedIn() ? (
          <AuthenticatedUserProfile />
        ) : (
          <div ref={userDropdownRef} style={{ position: "relative" }}>
            <UserIcon
              className="mx-2"
              size={isMobile ? "2rem" : "2.5rem"}
              color="black"
              onClick={handleUserIconClick}
            />
            {showUserDropdown && (
              <UserDropdown
                isVisible={true}
                onClose={() => setShowUserDropdown(false)}
                onNavigateToProfile={navigateToProfile}
              />
            )}
          </div>
        )}
        <div
          onClick={() => navigate("/profile/favorites", { state: "favorites" })}
          style={{ cursor: "pointer" }}
        >
          <HeartIcon
            className="mx-2"
            size={isMobile ? "2rem" : "2.5rem"}
            color="black"
            filled={false}
          />
        </div>
        <Dropdown align="end" onToggle={(isOpen) => setShowArrow(isOpen)}>
          <Dropdown.Toggle as={CustomToggle} id="cart-dropdown" />
          {showArrow && <div className="cart-arrow"></div>}
          <Dropdown.Menu
            className={`cart-dropdown-menu ${
              cartItems.length === 0 ? "empty-cart" : ""
            }`}
          >
            {cartItems.length > 0 ? (
              <>
                <div className="my-cart">Mi Carrito</div>

                {cartItems.map((item, index) => (
                  <div
                    key={`${item.productId}-${item.colorIndex}-${item.sizeId}`}
                  >
                    <div className="cart-item">
                      <div className="cart-item-image">
                        <img src={item.image} alt={item.name} />
                      </div>
                      <div className="cart-item-details">
                        <h6>{item.name}</h6>
                        <div className="color-size-info">
                          <span
                            className="color-dot"
                            style={{ backgroundColor: item.color }}
                          />
                          <span>{item.colorName}</span>
                          <span>/</span>
                          <span>{item.sizeName}</span>
                        </div>
                        <div className="quantity">
                          Cantidad: {item.quantity}
                        </div>
                      </div>
                      <div className="cart-item-actions">
                        <button
                          className="delete-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeItem(
                              item.productId,
                              item.colorIndex,
                              item.sizeId,
                              item.sizeProductId
                            );
                          }}
                        >
                          <FontAwesomeIcon icon={faTrashCan} />
                        </button>
                        <div className="price-container">
                          {item.discount && item.discount > 0 ? (
                            <>
                              <span className="original-price">
                                $
                                {(
                                  item.originalPrice * item.quantity
                                ).toLocaleString("es-MX", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                              <span className="discounted-price">
                                $
                                {(
                                  item.finalPrice * item.quantity
                                ).toLocaleString("es-MX", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            </>
                          ) : (
                            <span className="price">
                              $
                              {(item.price * item.quantity).toLocaleString(
                                "es-MX",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        width: "calc(100% - 1.5rem)",
                        margin: "0rem 0.5rem",
                        height: "1px",
                        backgroundImage:
                          "linear-gradient(to right, #614900 50%, transparent 50%)",
                        backgroundSize: "30px 2px",
                        backgroundRepeat: "repeat-x",
                      }}
                    />
                  </div>
                ))}

                <div className="cart-footer">
                  <div className="cart-total">
                    <span style={{ marginRight: "2rem" }}>Total:</span>
                    <span className="total-amount">
                      $
                      {cartItems
                        .reduce(
                          (total, item) =>
                            total + item.finalPrice * item.quantity,
                          0
                        )
                        .toLocaleString("es-MX", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                    </span>
                  </div>
                  <div className="view-cart-button-div">
                    <button
                      className="view-cart-button"
                      onClick={() => navigate("/cart")}
                    >
                      Ver mi carrito
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="empty-cart-message">
                <p>
                  Agrega artículos al
                  <br />
                  carrito para realizar
                  <br />
                  tu compra
                </p>
                <div className="divider">
                  <span className="line"></span>
                  <span className="circle">ó</span>
                  <span className="line"></span>
                </div>
                <p>
                  Inicia sesión para ver
                  <br />
                  tu carrito de compra
                </p>
              </div>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* Mobile Search Modal */}
      <Modal
        show={showMobileSearch}
        onHide={() => setShowMobileSearch(false)}
        centered
        className="mobile-search-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Buscar Productos</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleMobileSearch}>
            <InputGroup>
              <FormControl
                type="text"
                placeholder="Buscar productos"
                value={mobileSearchTerm}
                onChange={(e) => setMobileSearchTerm(e.target.value)}
                autoFocus
              />
              <Button type="submit" variant="outline-secondary">
                <FontAwesomeIcon icon={faSearch} />
              </Button>
            </InputGroup>
          </form>
        </Modal.Body>
      </Modal>

      {/* Mobile Filter Sidebar */}
      {showMobileFilters && (
        <>
          <div
            className="mobile-filter-overlay"
            onClick={() => setShowMobileFilters(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 9998,
            }}
          />
          <div
            className="mobile-filter-sidebar"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "280px",
              height: "100vh",
              backgroundColor: "#FBE584",
              zIndex: 9999,
              overflowY: "auto",
              padding: "20px",
              boxShadow: "2px 0 10px rgba(0,0,0,0.1)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h3 style={{ margin: 0, fontWeight: "bold" }}>Filtros</h3>
              <FontAwesomeIcon
                icon={faTimes}
                style={{ cursor: "pointer", fontSize: "1.5rem" }}
                onClick={() => setShowMobileFilters(false)}
              />
            </div>

            <div style={{ flex: 1, overflowY: "auto", paddingRight: "10px" }}>
              {/* Search Input */}
              <div className="mb-4">
                <label
                  style={{
                    fontWeight: "bold",
                    marginBottom: "10px",
                    display: "block",
                  }}
                >
                  Buscar
                </label>
                <InputGroup>
                  <FormControl
                    type="text"
                    placeholder="Buscar productos"
                    value={mobileSearchTerm}
                    onChange={(e) => setMobileSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </div>

              {/* Categories */}
              <div className="mb-4">
                <h4 style={{ fontWeight: "bold", marginBottom: "15px" }}>
                  Categorías
                </h4>
                <div>
                  {categories.map((category) => (
                    <div
                      key={category.id}
                      className="d-flex justify-content-between align-items-center mb-2"
                    >
                      <label
                        htmlFor={`mobile-category-${category.id}`}
                        style={{ cursor: "pointer", flex: 1 }}
                      >
                        {category.name}
                      </label>
                      <input
                        type="checkbox"
                        id={`mobile-category-${category.id}`}
                        checked={selectedCategories.includes(category.id)}
                        onChange={() => handleCategoryChange(category.id)}
                        style={{ marginLeft: "10px" }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Sizes */}
              <div className="mb-4">
                <h4 style={{ fontWeight: "bold", marginBottom: "15px" }}>
                  Tallas
                  {isFilteringSizes && (
                    <span style={{ fontSize: "0.8rem", color: "#666", marginLeft: "0.5rem" }}>
                      (Filtrando...)
                    </span>
                  )}
                </h4>
                {selectedCategories.length > 0 ? (
                  <div>
                    {sizes.length > 0 ? (
                      sizes.map((size) => (
                        <div
                          key={size.id}
                          className="d-flex justify-content-between align-items-center mb-2"
                        >
                          <label
                            htmlFor={`mobile-size-${size.id}`}
                            style={{ cursor: "pointer", flex: 1 }}
                          >
                            {size.name}
                          </label>
                          <input
                            type="checkbox"
                            id={`mobile-size-${size.id}`}
                            checked={selectedSizes.includes(size.id)}
                            onChange={() => handleSizeChange(size.id)}
                            style={{ marginLeft: "10px" }}
                          />
                        </div>
                      ))
                    ) : (
                      <div style={{ 
                        textAlign: "center", 
                        color: "#666", 
                        fontStyle: "italic",
                        padding: "1rem 0"
                      }}>
                        No hay tallas disponibles para las categorías seleccionadas
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: "center", 
                    color: "#666", 
                    fontStyle: "italic",
                    padding: "1rem 0"
                  }}>
                    Selecciona una categoría para ver las tallas disponibles
                  </div>
                )}
              </div>

              {/* Price Range */}
              <div className="mb-4">
                <h4 style={{ fontWeight: "bold", marginBottom: "15px" }}>
                  Rango de Precio
                </h4>
                <Slider
                  value={priceRange}
                  onChange={handlePriceRangeChange}
                  min={0}
                  max={5000}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `$${value}`}
                  style={{ color: "#997404", marginBottom: "20px" }}
                />
                <div className="d-flex justify-content-between">
                  <span style={{ fontWeight: "bold" }}>${priceRange[0]}</span>
                  <span style={{ fontWeight: "bold" }}>${priceRange[1]}</span>
                </div>
              </div>
            </div>

            {/* Apply Button */}
            <Button
              onClick={applyFilters}
              style={{
                backgroundColor: "#997404",
                borderColor: "#997404",
                width: "100%",
                padding: "12px",
                fontWeight: "bold",
                marginTop: "1rem",
              }}
            >
              Aplicar Filtros
            </Button>
          </div>
        </>
      )}
    </Navbar>
  );
};

export default CustomNavbarClients;
